/**
 * Ticket Real-Time Stream Composable
 *
 * Subscribes to ticket:event via Socket.IO and provides
 * callback hooks for each event type. Includes polling fallback
 * when Socket.IO is disconnected.
 */

import { watch, onUnmounted, ref } from 'vue'
import { useSocket } from './useSocket'
import logger from '@/utils/logger'

interface TicketEventPayload {
  event: string
  clientId: string | null
  data: Record<string, any>
  timestamp: string
  source: string
  _subject: string
}

type TicketEventCallback = (_data: TicketEventPayload['data'], _payload: TicketEventPayload) => void

export function useTicketStream() {
  const { on, off, isConnected } = useSocket()
  const listeners = ref<Map<string, Set<TicketEventCallback>>>(new Map())

  function addListener(eventType: string, callback: TicketEventCallback) {
    if (!listeners.value.has(eventType)) {
      listeners.value.set(eventType, new Set())
    }
    listeners.value.get(eventType)!.add(callback)
  }

  function removeListener(eventType: string, callback: TicketEventCallback) {
    listeners.value.get(eventType)?.delete(callback)
  }

  // Central handler for all ticket:event messages
  function handleTicketEvent(payload: TicketEventPayload) {
    const eventType = payload.event
    const callbacks = listeners.value.get(eventType)
    if (callbacks) {
      callbacks.forEach(cb => {
        try {
          cb(payload.data, payload)
        } catch (err) {
          logger.error('[useTicketStream] Callback error:', err)
        }
      })
    }

    // Also fire wildcard listeners
    const wildcardCallbacks = listeners.value.get('*')
    if (wildcardCallbacks) {
      wildcardCallbacks.forEach(cb => {
        try {
          cb(payload.data, payload)
        } catch (err) {
          logger.error('[useTicketStream] Wildcard callback error:', err)
        }
      })
    }
  }

  // Register Socket.IO listener
  on('ticket:event', handleTicketEvent)

  onUnmounted(() => {
    off('ticket:event', handleTicketEvent)
    listeners.value.clear()
  })

  // Polling fallback
  let fallbackTimer: ReturnType<typeof setInterval> | null = null
  let fallbackFn: (() => void) | null = null

  function setFallback(pollFn: () => void, intervalMs = 30000) {
    fallbackFn = pollFn
    // Watch connection state to toggle polling
    watch(isConnected, (connected) => {
      if (!connected && fallbackFn) {
        if (!fallbackTimer) {
          fallbackTimer = setInterval(fallbackFn, intervalMs)
        }
      } else {
        if (fallbackTimer) {
          clearInterval(fallbackTimer)
          fallbackTimer = null
        }
        // Catch up on reconnect
        if (connected && fallbackFn) {
          fallbackFn()
        }
      }
    }, { immediate: true })
  }

  onUnmounted(() => {
    if (fallbackTimer) {
      clearInterval(fallbackTimer)
      fallbackTimer = null
    }
  })

  // Convenience subscription helpers
  function onTicketCreated(callback: TicketEventCallback) {
    addListener('ticket.created', callback)
    return () => removeListener('ticket.created', callback)
  }

  function onTicketUpdated(callback: TicketEventCallback) {
    addListener('ticket.updated', callback)
    return () => removeListener('ticket.updated', callback)
  }

  function onTicketMessage(callback: TicketEventCallback) {
    addListener('ticket.message', callback)
    return () => removeListener('ticket.message', callback)
  }

  function onTicketAssigned(callback: TicketEventCallback) {
    addListener('ticket.assigned', callback)
    return () => removeListener('ticket.assigned', callback)
  }

  function onTicketStatusChanged(callback: TicketEventCallback) {
    addListener('ticket.status', callback)
    return () => removeListener('ticket.status', callback)
  }

  function onAnyTicketEvent(callback: TicketEventCallback) {
    addListener('*', callback)
    return () => removeListener('*', callback)
  }

  return {
    onTicketCreated,
    onTicketUpdated,
    onTicketMessage,
    onTicketAssigned,
    onTicketStatusChanged,
    onAnyTicketEvent,
    setFallback,
    isConnected,
  }
}
